import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { GetVirtualBankAccountsAccountNumberMovementsFilterEntryType } from 'api/customerApi';

export interface FiltersState {
  entry_type:
    | 'All'
    | GetVirtualBankAccountsAccountNumberMovementsFilterEntryType;
}

type PaginationState = {
  includeCursor?: boolean;
  cursorBefore: string | null;
  cursorAfter: string | null;
};

export type LocalState = PaginationState & {
  filters: FiltersState;
};

type Action =
  | { type: 'setCursorBefore'; value: string | null }
  | { type: 'setCursorAfter'; value: string | null }
  | { type: 'setFilters'; value: FiltersState }
  | { type: 'RESET'; value: LocalState };

function reducer(state: LocalState, action: Action): LocalState {
  switch (action.type) {
    case 'setFilters':
      return {
        ...state,
        cursorBefore: null,
        cursorAfter: null,
        filters: action.value
      };
    case 'setCursorBefore':
      return {
        ...state,
        cursorBefore: action.value,
        cursorAfter: null,
        includeCursor: undefined
      };
    case 'setCursorAfter':
      return {
        ...state,
        cursorBefore: null,
        cursorAfter: action.value,
        includeCursor: undefined
      };
    case 'RESET':
      return action.value;
  }
}

function computeInitialState(
  searchParams: URLSearchParams,
  defaultState?: Partial<LocalState>
): LocalState {
  return {
    filters: {
      entry_type:
        (searchParams.get('entry_type') as FiltersState['entry_type']) || 'All'
    },
    includeCursor: defaultState?.includeCursor || undefined,
    cursorBefore:
      defaultState?.cursorBefore || searchParams.get('cursorBefore') || null,
    cursorAfter:
      defaultState?.cursorAfter || searchParams.get('cursorAfter') || null
  };
}

export function useLocalState(
  defaultState?: Partial<LocalState>,
  key?: string
) {
  const [searchParams] = useSearchParams();

  // Lazy initializer for useReducer
  const [state, dispatch] = useReducer(reducer, null, () =>
    computeInitialState(searchParams, defaultState)
  );

  // Reinitialize when the key (accountNumber) changes
  useEffect(() => {
    if (key) {
      dispatch({
        type: 'RESET',
        value: computeInitialState(searchParams, defaultState)
      });
    }
  }, [key, searchParams]); // <-- key = accountNumber in this case

  const updateCursorAfter = useCallback(
    (value: string | null) => dispatch({ type: 'setCursorAfter', value }),
    []
  );

  const updateFilters = useCallback(
    (value: FiltersState) => dispatch({ type: 'setFilters', value }),
    []
  );

  const queryParams = useMemo(
    () => ({
      cursorAfter: state.cursorAfter,
      cursorBefore: state.cursorBefore,
      ...state.filters
    }),
    [state]
  );

  const resetState = useCallback(() => {
    dispatch({ type: 'setFilters', value: { entry_type: 'All' } });
  }, []);

  return {
    state,
    queryParams,
    resetState,
    dispatch,
    updateCursorAfter,
    updateFilters
  };
}
